import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, Terminal, MessageSquare, Wifi, WifiOff, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { format } from 'date-fns';
import { useDateLocale } from '@/hooks/useDateLocale';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { fetchCommands } from '@/lib/reseller-api';
import { fetchDevices, getCommandResultContent } from '@/lib/traccar-api';
import { useTraccarWebSocket } from '@/hooks/useTraccarWebSocket';
import { SendCommandDialog } from '@/components/devices/SendCommandDialog';

const PAGE_SIZE = 50;

export default function Commands() {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();

  const { devices: liveDevices, events, connect, disconnect } = useTraccarWebSocket();
  useEffect(() => {
    console.info('[commands] opening WebSocket');
    connect();
    return () => {
      console.info('[commands] closing WebSocket');
      disconnect();
    };
  }, [connect, disconnect]);

  const { data: initialDevices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    staleTime: 60000,
  });

  const devicesById = useMemo(() => {
    const devices = new Map(initialDevices.map((device) => [device.id, device]));
    liveDevices.forEach((device) => devices.set(device.id, device));
    return devices;
  }, [initialDevices, liveDevices]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ['commands', page, search],
    queryFn: () => fetchCommands({ page, limit: PAGE_SIZE, search }),
  });

  const commands = useMemo(() => data?.data ?? [], [data?.data]);
  const incomingResults = useMemo(() => {
    const byId = new Map(events
      .filter(event => event.type === 'commandResult')
      .map(event => [event.id, event]));
    return Array.from(byId.values());
  }, [events]);
  const rows = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    const incomingRows = incomingResults
      .filter(event => {
        if (!lowerSearch) return true;
        const deviceName = devicesById.get(event.deviceId)?.name ?? '';
        return deviceName.toLowerCase().includes(lowerSearch)
          || getCommandResultContent(event).toLowerCase().includes(lowerSearch);
      })
      .map(event => ({
        id: `incoming-${event.id}`,
        direction: 'incoming' as const,
        deviceId: event.deviceId,
        deviceName: devicesById.get(event.deviceId)?.name ?? `#${event.deviceId}`,
        content: getCommandResultContent(event) || '—',
        viaSms: false,
        phoneNumber: null,
        timestamp: event.eventTime,
      }));
    const outgoingRows = commands.map(command => ({
      id: `outgoing-${command.id}`,
      direction: 'outgoing' as const,
      deviceId: command.traccar_device_id,
      deviceName: command.device_name ?? `#${command.traccar_device_id}`,
      content: command.command_data ?? command.command_type,
      viaSms: command.via_sms,
      phoneNumber: command.phone_number,
      timestamp: command.sent_at,
    }));
    return [...incomingRows, ...outgoingRows]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [commands, devicesById, incomingResults, search]);
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <div className="flex items-center gap-2 flex-1">
          <Terminal className="h-6 w-6" />
          <h1 className="text-2xl font-bold flex-1">{t('nav.commands')}</h1>
          <SendCommandDialog
            trigger={
              <Button size="sm" className="gap-1">
                <Terminal className="h-4 w-4" />
                {t('devices.sendCommand')}
              </Button>
            }
          />
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('common.search')}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {isLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t('common.loading')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('commands.direction', { defaultValue: 'Direction' })}</TableHead>
                    <TableHead>{t('devices.name')}</TableHead>
                    <TableHead>{t('devices.status')}</TableHead>
                    <TableHead>{t('devices.commandData')} / {t('commands.result', { defaultValue: 'Result' })}</TableHead>
                    <TableHead>{t('devices.sendViaSms')}</TableHead>
                    <TableHead>{t('history.start')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.direction === 'outgoing' ? (
                          <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300">
                            <ArrowUpRight className="h-3 w-3" />
                            {t('commands.outgoing', { defaultValue: 'Outgoing' })}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                            <ArrowDownLeft className="h-3 w-3" />
                            {t('commands.incoming', { defaultValue: 'Incoming' })}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{row.deviceName}</TableCell>
                      <TableCell>
                        {devicesById.get(row.deviceId)?.status === 'online' ? (
                          <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                            <Wifi className="h-3 w-3" />
                            {t('devices.online')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-muted-foreground">
                            <WifiOff className="h-3 w-3" />
                            {t(`devices.${devicesById.get(row.deviceId)?.status ?? 'unknown'}`)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground max-w-[320px] break-all">
                        {row.content}
                      </TableCell>
                      <TableCell>
                        {row.viaSms && (
                          <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300">
                            <MessageSquare className="h-3 w-3" />
                            SMS{row.phoneNumber ? ` · ${row.phoneNumber}` : ''}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm" title={row.timestamp}>
                        {format(new Date(row.timestamp), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        {t('common.noData')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-muted-foreground">
                  {t('common.showingRange', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                    {t('common.previous')}
                  </Button>
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
